import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {Inject} from 'inversify-props';

import {ILogger} from '@/utils/ILogger';
import {LogdownLogger} from '@/utils/LogdownLogger';

import {ISettingsService} from '@/services/settings/ISettings.service';
import {Setting} from '@/services/settings/Settings.types';
import {IApiService} from '@/services/IApi.service';

@Module({name: 'ApiStore', namespaced: true})
export class ApiStore extends VuexModule {

  @Inject(Symbol.for('ISettingsService'))
  private settingsService!: ISettingsService;

  @Inject(Symbol.for('IApiService'))
  private apiService!: IApiService;

  private remote: string = '';
  private secured: boolean = false;
  private available: boolean = false;
  private checkSuspended: boolean = false;

  private logger: ILogger = new LogdownLogger('ApiStore');

  /* REMOTE */

  @Action({commit: 'setRemote', rawError: true})
  public loadRemote(): string {

    let remote = window.location.host;
    if (this.ConfigurableRemote) {
      const configuredRemote = this.settingsService.get(Setting.RemoteAddress);

      if (configuredRemote !== null) {
        remote = configuredRemote;
      }
    }

    return remote;
  }

  @Mutation
  public setRemote(remote: string): void {
    this.logger.debug(`Changing remote to ${remote}`);
    this.remote = remote;
  }

  get Remote(): string {
    return this.remote;
  }

  /* SECURED */

  @Action({commit: 'setSecured', rawError: true})
  public loadSecured(): boolean {

    let secured = window.location.protocol.includes('https');
    if (this.ConfigurableRemote) {
      const configuredSecured = (this.settingsService.get(Setting.Secured) === 'true');
      if (configuredSecured !== null) {
        secured = configuredSecured;
      }
    }

    return secured;
  }

  @Mutation
  public setSecured(secured: boolean): void {
    this.secured = secured;
  }

  get Secured(): boolean {
    return this.secured;
  }

  /* AVAILABLE */

  @Action({commit: 'setAvailable', rawError: true})
  public async checkAvailable(timeoutMs: number = 5000): Promise<boolean> {
    this.logger.trace(`Checking API availability`);
    const available = await this.apiService.check(timeoutMs);
    this.logger.trace(`API availability updated`, available);

    return available;
  }

  @Mutation
  public setAvailable(available: boolean): void {
    this.available = available;
  }

  get Available(): boolean {
    return this.available;
  }


  /* CHECK SUSPENDED */

  @Mutation
  public setCheckSuspended(suspended: boolean): void {
    this.checkSuspended = suspended;
  }

  get CheckSuspended() {
    return this.checkSuspended;
  }

  /* Configurable */
  get ConfigurableRemote(): boolean {
    return process.env.VUE_APP_CONFIGURABLE_REMOTE === 'true';
  }

}
